import {
  CAMPAIGN_EVENT_GROUP_KEYS,
  useCampaignEvents,
  type CampaignEventDto,
  type CampaignEventGroup,
} from './events-queries';
import type { CampaignRecord } from './queries';

import { EventTypeSelect } from '@/components/event-type-select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { useListQueryRoute } from '@/lib/use-list-query-route';

export interface LogsPanelProps {
  campaignId: string;
  status: CampaignRecord['status'];
  routeId?: string;
}

interface LogsFilter {
  types?: string | undefined;
}

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_CAP = 200;

const parseGroups = (raw: string | undefined): CampaignEventGroup[] => {
  if (!raw) return [];
  const out: CampaignEventGroup[] = [];
  for (const part of raw.split(',')) {
    const trimmed = part.trim();
    if (trimmed && (CAMPAIGN_EVENT_GROUP_KEYS as readonly string[]).includes(trimmed)) {
      out.push(trimmed as CampaignEventGroup);
    }
  }
  return out;
};

const formatTimestamp = (iso: string): string => {
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  return sameDay ? d.toLocaleTimeString('ru-RU', { hour12: false }) : d.toLocaleString('ru-RU');
};

const metadataPreview = (md: CampaignEventDto['metadata']): string => {
  if (!md) return '';
  const keys = Object.keys(md).slice(0, 3);
  if (keys.length === 0) return '';
  return keys.map((k) => `${k}=${JSON.stringify(md[k])}`).join(' ');
};

export const LogsPanel = ({
  campaignId,
  status,
  routeId = '/_protected/campaigns/$campaignId',
}: LogsPanelProps) => {
  const lq = useListQueryRoute<LogsFilter, 'occurredAt'>({
    from: routeId,
    defaultFilter: { types: undefined as string | undefined },
    defaultSort: { field: 'occurredAt', dir: 'desc' },
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const groups = parseGroups(lq.filter.types);
  const pageSize = Math.min(Math.max(1, lq.pageSize), PAGE_SIZE_CAP);

  const query = useCampaignEvents(campaignId, { page: lq.page, pageSize, groups }, status);

  const totalPages = query.data ? Math.max(0, Math.ceil(query.data.total / pageSize)) : 0;

  const handleGroupsChange = (next: CampaignEventGroup[]): void => {
    lq.updateQuery({ filter: { types: next.length > 0 ? next.join(',') : undefined } });
  };

  return (
    <div className="space-y-3 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <EventTypeSelect
          groups={CAMPAIGN_EVENT_GROUP_KEYS}
          selected={groups}
          onChange={handleGroupsChange}
        />
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            void query.refetch();
          }}
          disabled={query.isFetching}
        >
          Обновить
        </Button>
      </header>

      {query.isPending ? (
        <div className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
        </div>
      ) : query.isError ? (
        <p className="text-sm text-destructive">Не удалось загрузить события.</p>
      ) : query.data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Пока нет событий.</p>
      ) : (
        <ul className="divide-y rounded border">
          {query.data.items.map((event) => {
            const preview = metadataPreview(event.metadata);
            const hasMetadata = event.metadata !== null && Object.keys(event.metadata).length > 0;
            return (
              <li key={event.id} className="px-3 py-2 text-sm">
                <div className="flex flex-wrap items-baseline gap-2">
                  <time
                    className="font-mono text-xs text-muted-foreground"
                    dateTime={event.occurredAt}
                    title={event.occurredAt}
                  >
                    {formatTimestamp(event.occurredAt)}
                  </time>
                  <Badge variant="outline">{event.eventType}</Badge>
                  <span className="text-xs text-muted-foreground">{event.userId ?? 'system'}</span>
                  {preview ? (
                    <span className="truncate text-xs text-muted-foreground">{preview}</span>
                  ) : null}
                </div>
                {hasMetadata ? (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-xs text-muted-foreground">
                      metadata
                    </summary>
                    <pre className="mt-1 overflow-x-auto rounded bg-muted p-2 text-xs">
                      {JSON.stringify(event.metadata, null, 2)}
                    </pre>
                  </details>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <footer className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
        <span>{query.data ? `Всего: ${String(query.data.total)}` : ''}</span>
        <Pagination
          page={lq.page}
          totalPages={totalPages}
          onPageChange={(page) => {
            lq.updateQuery({ page });
          }}
        />
      </footer>
    </div>
  );
};
